import { ExternalLink, Home, Rocket } from 'lucide-react';
import { Link, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@bliks-io/ui';

/**
 * AuthenticatedNotFound Component
 *
 * "Coming Soon" page for authenticated routes (within /$slug).
 * Renders within AppLayout - sidebar, header, and breadcrumb are already present.
 * Displays an encouraging message that the feature is in development.
 *
 * @example
 * Used as notFoundComponent in route configuration:
 * ```tsx
 * export const Route = createFileRoute('/$slug')({
 *   notFoundComponent: AuthenticatedNotFound
 * });
 * ```
 */
export function AuthenticatedNotFound() {
  const { t } = useTranslation();
  const params = useParams({ strict: false });
  const slug = params.slug || '';

  return (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <Card className="max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-primary" />
            <CardTitle>{t('common.NotFound.title')}</CardTitle>
          </div>
          <CardDescription>{t('common.NotFound.description')}</CardDescription>
        </CardHeader>

        <CardContent className="flex flex-col items-center text-center space-y-4">
          <img
            className="w-full h-auto max-w-[200px]"
            src="/assets/images/coming-soon.png"
            alt="Coming Soon"
          />

          <div className="inline-flex items-center rounded-sm border border-danger-line bg-danger-surface px-2 py-1 text-xs font-normal text-danger select-none">
            {t('common.Sidebar.ComingSoon')}
          </div>

          <p className="text-sm text-muted-foreground max-w-md">
            {t('common.NotFound.suggestion')}
          </p>
        </CardContent>

        <CardFooter className="flex flex-wrap gap-2 justify-center">
          <Link to="/$slug" params={{ slug }}>
            <Button variant="primary" className="gap-2">
              <Home className="h-4 w-4" />
              {t('common.NotFound.goToDashboard')}
            </Button>
          </Link>

          <Button
            variant="outline"
            className="gap-2"
            onClick={() => window.open('https://bliks.io/roadmap', '_blank')}
          >
            {t('common.NotFound.viewRoadmap')}
            <ExternalLink className="h-4 w-4" />
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
